import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { getDataAPI, putDataAPI, postDataAPI } from '../../utils/fetchData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
import { REMINDER_TEMPLATES } from '../../utils/remindersData';

const WorkflowDetail = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { workflowId } = useParams();
  
  const [workflow, setWorkflow] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeTab, setActiveTab] = useState('all'); // all, overdue, due-today, completed
  const [showStepSettings, setShowStepSettings] = useState(null);
  const [editingStep, setEditingStep] = useState(null);
  const [editForm, setEditForm] = useState({
    title: '',
    dueDate: '',
    description: ''
  });

  useEffect(() => {
    loadWorkflow();
  }, [workflowId]);

  const loadWorkflow = async () => {
    try {
      console.log('🔍 WorkflowDetail - Loading workflow with ID:', workflowId);
      
      // Load reminders from backend API
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await getDataAPI('user/reminders', token);
      
      if (response?.status !== 200 && !response?.data?.success) {
        console.error('❌ Failed to load reminders:', response);
        throw new Error('Failed to load reminders');
      }
      
      const allReminders = response.data.data || [];
      console.log('🔍 WorkflowDetail - After API call, got', allReminders.length, 'reminders');
      console.log('🔍 WorkflowDetail - Sample reminders:', allReminders.slice(0, 3).map(r => ({
        id: r._id,
        releaseId: r.releaseId,
        releaseName: r.releaseName,
        title: r.title,
        type: r.type,
        metadata: r.metadata
      })));
      
      // Try to find workflow steps by releaseId first, then by workflowId in metadata
      let workflowSteps = allReminders.filter(r => r.releaseId === workflowId);
      console.log('🔍 WorkflowDetail - Found by releaseId:', workflowSteps.length);
      
      // If not found by releaseId, try by workflowId in metadata
      if (workflowSteps.length === 0) {
        workflowSteps = allReminders.filter(r => r.metadata?.workflowId === workflowId);
        console.log('🔍 WorkflowDetail - Found by metadata.workflowId:', workflowSteps.length);
      }
      
      if (workflowSteps.length === 0) {
        console.error('❌ Workflow not found with ID:', workflowId);
        console.log('🔍 Available reminders:', allReminders.map(r => ({ 
          id: r._id, 
          releaseId: r.releaseId, 
          workflowId: r.metadata?.workflowId,
          releaseName: r.releaseName,
          title: r.title 
        })));
        dispatch(setErrorAlert('Workflow not found'));
        navigate('/flows');
        return;
      }
      
      // Determine workflow type from the first step (all steps should have the same type)
      const firstStep = workflowSteps[0];
      const hasReleaseName = firstStep?.releaseName && workflowSteps.length > 0;
      
      // Check if this is a custom workflow
      const isCustomWorkflow = firstStep?.type === 'custom' || 
                               firstStep?.metadata?.custom === true ||
                               firstStep?.metadata?.workflowType === 'custom';
      
      // Check if any step has booking/show indicators
      const hasBookingType = workflowSteps.some(s => 
        s.type === 'booking' || 
        s.metadata?.type === 'booking' || 
        s.metadata?.workflowType === 'upcoming-show' ||
        s.metadata?.workflowType === 'upcoming_show' ||
        s.metadata?.workflowType === 'booking'
      );
      
      const workflowType = firstStep?.metadata?.workflowType || 
                          firstStep?.metadata?.type || 
                          firstStep?.type || 
                          (hasBookingType ? 'booking' : (hasReleaseName ? 'release' : null));
      
      console.log('🔍 WorkflowDetail - Detected workflow type:', workflowType);
      console.log('🔍 WorkflowDetail - First step data:', {
        type: firstStep?.type,
        metadata: firstStep?.metadata,
        templateId: firstStep?.templateId,
        releaseName: firstStep?.releaseName,
        hasBookingType,
        hasReleaseName
      });
      
      // Map backend fields to frontend format
      const mappedSteps = workflowSteps
        .filter((step) => {
          // Filter out deprecated steps from existing workflows
          const templateId = step.templateId || step.metadata?.templateId || '';
          const templateIdLower = templateId.toLowerCase();
          // Remove old ONE_WEEK_OUT and DISTRIBUTOR_UPLOAD steps
          return templateIdLower !== 'one_week_out' && 
                 templateIdLower !== 'distributor_upload';
        })
        .map((step) => {
          // Look up template by templateId to get the proper title
          const templateId = step.templateId || step.metadata?.templateId;
          let template = null;
          
          if (templateId) {
            // Find template by matching id (case-insensitive)
            const templateKey = Object.keys(REMINDER_TEMPLATES).find(
              key => REMINDER_TEMPLATES[key].id.toLowerCase() === templateId.toLowerCase()
            );
            if (templateKey) {
              template = REMINDER_TEMPLATES[templateKey];
            }
          }
          
          return {
            id: step._id,
            releaseId: step.releaseId,
            releaseName: step.releaseName,
            releaseDate: step.releaseDate,
            title: step.title,
            description: step.description,
            status: step.status,
            dueDate: step.dueDate,
            type: step.type,
            templateId: templateId,
            template: template,
            metadata: step.metadata || {}
          };
        });
      
      // Sort by due date
      const sortedSteps = mappedSteps.sort((a, b) => 
        new Date(a.dueDate) - new Date(b.dueDate)
      );
      
      // After sorting, apply template titles based on workflow type and sorted order
      const finalSteps = sortedSteps.map((step, sortedIndex) => {
        // For custom workflows, use the stored title/description directly (don't override with templates)
        if (isCustomWorkflow) {
          return {
            ...step,
            title: step.title || 'Workflow Step',
            description: step.description || ''
          };
        }
        
        let template = step.template;
        
        // Check if it's a release workflow (has releaseName and not booking type)
        const isRelease = (workflowType === 'release' || 
                         workflowType === 'song_release' ||
                         (!hasBookingType && step.releaseName)) && 
                         !hasBookingType;
        
        // Check if it's a booking/show workflow
        const isBooking = workflowType === 'booking' || 
                         workflowType === 'upcoming-show' || 
                         workflowType === 'upcoming_show' ||
                         hasBookingType;
        
        // For song release workflows, always map by position to ensure correct order
        // This fixes existing workflows that may have incorrect templateIds
        if (isRelease && !isCustomWorkflow) {
          const releaseTemplates = [
            REMINDER_TEMPLATES.FIVE_WEEKS_OUT,      // Step 1: Publisher & PRO Notification
            REMINDER_TEMPLATES.FOUR_WEEKS_OUT,      // Step 2: DSP Submission
            REMINDER_TEMPLATES.THREE_WEEKS_OUT,     // Step 3: Spotify for Artists Confirmation
            REMINDER_TEMPLATES.TWO_WEEKS_OUT,       // Step 4: Final Pitch Deadline
            REMINDER_TEMPLATES.FORTY_EIGHT_HOURS_OUT, // Step 5: Canvas & Visualizer Check
            REMINDER_TEMPLATES.RELEASE_DAY,         // Step 6: Song Release Announcement
            REMINDER_TEMPLATES.SEVEN_DAYS_POST,     // Step 7: Release Performance Recap
            REMINDER_TEMPLATES.FOURTEEN_DAYS_POST   // Step 8: PRO & Publisher Registration
          ];
          // Use position-based mapping to ensure correct order
          // Only map if we have a template at this position, otherwise keep existing template
          if (sortedIndex < releaseTemplates.length) {
            template = releaseTemplates[sortedIndex];
          }
          console.log(`🔍 Mapped release step ${sortedIndex + 1} to template: ${template?.title || 'none'}`);
        } else if (isBooking && !isCustomWorkflow) {
          // For booking workflows, also map by position
          const showTemplates = [
            REMINDER_TEMPLATES.BOOKING_CONFIRMED,
            REMINDER_TEMPLATES.BOOKING_CONTRACTING,
            REMINDER_TEMPLATES.BOOKING_MARKETING_LAUNCH,
            REMINDER_TEMPLATES.BOOKING_SHOW_ADVANCE,
            REMINDER_TEMPLATES.BOOKING_TICKET_COUNTS,
            REMINDER_TEMPLATES.BOOKING_TRAVEL_FINALIZATION,
            REMINDER_TEMPLATES.BOOKING_GUEST_LIST,
            REMINDER_TEMPLATES.BOOKING_POST_SHOW_SETTLEMENT
          ];
          if (sortedIndex < showTemplates.length) {
            template = showTemplates[sortedIndex];
          }
          console.log(`🔍 Mapped booking step ${sortedIndex + 1} to template: ${template?.title || 'none'}`);
        } else if (!template) {
          // Fallback: try to infer from templateId if not already mapped
          console.log(`🔍 Step ${sortedIndex + 1}: No template found, using stored title/description`);
        }
        
        return {
          ...step,
          template: template,
          title: template?.title || step.title || 'Workflow Step',
          description: template?.description || step.description || ''
        };
      });
      
      // Log final steps for debugging
      console.log(`📊 Final workflow steps: ${finalSteps.length} steps`);
      finalSteps.forEach((step, idx) => {
        console.log(`  Step ${idx + 1}/8: ${step.title} (templateId: ${step.templateId || 'none'})`);
      });
      
      // For song release workflows, ensure we always show 8 steps
      // If we have fewer than 8, check which step is missing
      const isReleaseWorkflow = (workflowType === 'release' || 
                                workflowType === 'song_release' ||
                                (!hasBookingType && finalSteps[0]?.releaseName)) && 
                                !hasBookingType && !isCustomWorkflow;
      
      const expectedSteps = isReleaseWorkflow ? 8 : finalSteps.length;
      
      // For song release workflows, ensure all 8 steps exist
      // Check which steps are missing and create them
      if (isReleaseWorkflow) {
        const releaseDate = new Date(finalSteps[0]?.releaseDate);
        const releaseId = workflowId;
        const releaseName = finalSteps[0]?.releaseName;
        
        // Define all 8 expected steps with their template IDs and due date calculations
        const expectedSteps = [
          { templateId: 'FIVE_WEEKS_OUT', daysOffset: -35, template: REMINDER_TEMPLATES.FIVE_WEEKS_OUT },
          { templateId: 'FOUR_WEEKS_OUT', daysOffset: -28, template: REMINDER_TEMPLATES.FOUR_WEEKS_OUT },
          { templateId: 'THREE_WEEKS_OUT', daysOffset: -21, template: REMINDER_TEMPLATES.THREE_WEEKS_OUT },
          { templateId: 'TWO_WEEKS_OUT', daysOffset: -14, template: REMINDER_TEMPLATES.TWO_WEEKS_OUT },
          { templateId: 'FORTY_EIGHT_HOURS_OUT', daysOffset: -2, template: REMINDER_TEMPLATES.FORTY_EIGHT_HOURS_OUT },
          { templateId: 'RELEASE_DAY', daysOffset: 0, template: REMINDER_TEMPLATES.RELEASE_DAY },
          { templateId: 'SEVEN_DAYS_POST', daysOffset: 7, template: REMINDER_TEMPLATES.SEVEN_DAYS_POST },
          { templateId: 'FOURTEEN_DAYS_POST', daysOffset: 14, template: REMINDER_TEMPLATES.FOURTEEN_DAYS_POST }
        ];
        
        // Find missing steps
        const existingTemplateIds = finalSteps.map(step => {
          const templateId = step.templateId || step.metadata?.templateId || '';
          return templateId.toUpperCase();
        });
        
        const missingSteps = expectedSteps.filter(expected => 
          !existingTemplateIds.includes(expected.templateId)
        );
        
        if (missingSteps.length > 0) {
          console.warn(`⚠️ Missing ${missingSteps.length} step(s) in workflow. Found ${finalSteps.length} steps, expected 8. Auto-creating missing steps...`);
          
          // Auto-create all missing steps (async, don't block)
          (async () => {
            try {
              const token = JSON.parse(localStorage.getItem('dizeeToken'));
              let createdCount = 0;
              
              for (const missingStep of missingSteps) {
                // Calculate due date
                let dueDate;
                if (missingStep.daysOffset === 0) {
                  // Release day - set to 10am PST
                  dueDate = new Date(releaseDate);
                  dueDate.setHours(10, 0, 0, 0);
                } else {
                  dueDate = new Date(releaseDate.getTime() + (missingStep.daysOffset * 24 * 60 * 60 * 1000));
                }
                
                const stepData = {
                  releaseId: releaseId,
                  releaseName: releaseName,
                  releaseDate: finalSteps[0].releaseDate,
                  templateId: missingStep.templateId,
                  title: missingStep.template.title,
                  description: missingStep.template.description,
                  dueDate: dueDate.toISOString(),
                  status: 'pending',
                  type: 'release',
                  metadata: {
                    workflowType: 'song_release',
                    templateId: missingStep.templateId
                  }
                };
                
                // Create the reminder via API
                const response = await postDataAPI('user/reminders', stepData, token);
                
                if (response?.status === 200 || response?.status === 201 || response?.data?.success) {
                  createdCount++;
                  console.log(`✅ Created missing step: ${missingStep.template.title}`);
                } else {
                  console.error(`❌ Failed to create step ${missingStep.template.title}:`, response?.data?.message);
                }
              }
              
              if (createdCount > 0) {
                console.log(`✅ Successfully created ${createdCount} missing step(s). Reloading workflow...`);
                // Reload the workflow to show the new steps
                setTimeout(() => {
                  loadWorkflow();
                }, 1000); // Delay to ensure backend has processed
              }
            } catch (error) {
              console.error('❌ Error auto-creating missing steps:', error);
              // Don't block the workflow display if creation fails
            }
          })();
        }
      }
      
      const completedCount = finalSteps.filter(s => s.status === 'completed').length;
      const percentage = Math.round((completedCount / expectedSteps) * 100);
      
      console.log(`🎯 WorkflowDetail - Final step count: ${finalSteps.length} steps`);
      console.log(`📊 WorkflowDetail - All step titles:`, finalSteps.map((s, i) => `${i+1}. ${s.title}`));
      
      setWorkflow({
        id: workflowId,
        name: finalSteps[0].releaseName,
        date: finalSteps[0].releaseDate,
        type: finalSteps[0].type === 'booking' || finalSteps[0].metadata?.type === 'booking' ? 'show' : 'release',
        completedSteps: completedCount,
        totalSteps: expectedSteps, // Always show 8 for song release workflows
        percentage
      });
      
      setSteps(finalSteps);
      
    } catch (error) {
      console.error('Error loading workflow:', error);
      dispatch(setErrorAlert('Failed to load workflow'));
    }
  };

  const handleCompleteStep = async (stepId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const response = await putDataAPI(`user/reminders/${stepId}/status`, {
        status: 'completed'
      }, token);
      
      if (response?.status === 200 || response?.data?.success) {
        dispatch(setSuccessAlert('Step completed'));
        loadWorkflow();
      } else {
        throw new Error('Failed to complete step');
      }
    } catch (error) {
      console.error('Error completing step:', error);
      dispatch(setErrorAlert('Failed to complete step'));
    }
  };

  const handleSnoozeStep = async (stepId) => {
    try {
      const token = JSON.parse(localStorage.getItem('dizeeToken'));
      const snoozeUntil = new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours from now
      
      const response = await putDataAPI(`user/reminders/${stepId}/status`, {
        status: 'snoozed',
        snoozeUntil: snoozeUntil.toISOString()
      }, token);
      
      if (response?.status === 200 || response?.data?.success) {
        dispatch(setSuccessAlert('Step snoozed for 24 hours'));
        loadWorkflow();
      } else {
        throw new Error('Failed to snooze step');
      }
    } catch (error) {
      console.error('Error snoozing step:', error);
      dispatch(setErrorAlert('Failed to snooze step'));
    }
  };

  const handleEditClick = (step) => {
    setEditingStep(step.id);
    setEditForm({
      title: step.title || '',
      dueDate: step.dueDate.split('T')[0], // Format for date input
      description: step.description || ''
    });
    setShowStepSettings(null);
  };

  const handleSaveEdit = (stepId) => {
    try {
      const reminders = JSON.parse(localStorage.getItem('reminders') || '[]');
      const updatedReminders = reminders.map(reminder => {
        if (reminder.id === stepId) {
          return {
            ...reminder,
            template: {
              ...reminder.template,
              title: editForm.title,
              description: editForm.description
            },
            dueDate: new Date(editForm.dueDate).toISOString()
          };
        }
        return reminder;
      });
      
      localStorage.setItem('reminders', JSON.stringify(updatedReminders));
      window.dispatchEvent(new Event('localStorageUpdate'));
      
      dispatch(setSuccessAlert('Step updated successfully'));
      setEditingStep(null);
      loadWorkflow();
    } catch (error) {
      console.error('Error updating step:', error);
      dispatch(setErrorAlert('Failed to update step'));
    }
  };

  const handleCancelEdit = () => {
    setEditingStep(null);
    setEditForm({ title: '', dueDate: '', description: '' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntil = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = date - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return `${Math.abs(diffDays)} days overdue`;
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const getStepStatus = (step) => {
    if (step.status === 'completed') return 'completed';
    if (step.isOverdue && step.isOverdue()) return 'overdue';
    if (step.isDueToday && step.isDueToday()) return 'due-today';
    
    const daysUntil = Math.ceil((new Date(step.dueDate) - new Date()) / (1000 * 60 * 60 * 24));
    if (daysUntil <= 3) return 'urgent';
    
    return 'upcoming';
  };

  const filterSteps = () => {
    if (activeTab === 'all') return steps;
    if (activeTab === 'completed') return steps.filter(s => s.status === 'completed');
    if (activeTab === 'overdue') return steps.filter(s => getStepStatus(s) === 'overdue');
    if (activeTab === 'due-today') return steps.filter(s => getStepStatus(s) === 'due-today');
    return steps;
  };

  const getStepNumber = (step) => {
    // Extract step number from template ID or use index
    const match = step.templateId.match(/\d+/);
    return match ? match[0] : steps.indexOf(step) + 1;
  };

  if (!workflow) {
    return null;
  }

  const filteredSteps = filterSteps();

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-start">
            <div className="flex-1">
              <h1 className="text-white text-[14px]" style={{ fontWeight: 300 }}>
                {workflow.name.toUpperCase()}
              </h1>
              <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-1">
                {workflow.type === 'show' ? 'Show: ' : 'Release: '}{formatDate(workflow.date)}
              </p>
              <p className="text-[rgba(255,255,255,0.5)] text-[11px]">
                Step {workflow.completedSteps}/{workflow.totalSteps} · {workflow.percentage}% Complete
              </p>
            </div>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70"
              onClick={() => {
                const canGoBack = window.history.length > 1;
                if (canGoBack) {
                  navigate(-1);
                } else {
                  navigate('/');
                }
              }}
            >
              Go back
            </span>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex">
              {['all', 'overdue', 'due-today', 'completed'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`flex-1 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab === 'due-today' ? 'Due Today' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-4 right-4 h-[2px] bg-[#AAFF00]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="w-full px-4 flex-1 overflow-y-auto">

            {/* Steps List */}
            <div className="space-y-3">
              {filteredSteps.map((step, index) => {
                const status = getStepStatus(step);
                const isCompleted = step.status === 'completed';
                
                return (
                  <div
                    key={step.id}
                    className={`bg-black border rounded-lg p-4 transition-all ${
                      isCompleted 
                        ? 'border-green-500 border-opacity-30' 
                        : status === 'overdue'
                        ? 'border-red-500'
                        : status === 'due-today'
                        ? 'border-yellow-500'
                        : 'border-[rgba(255,255,255,0.1)]'
                    }`}
                  >
                    {editingStep === step.id ? (
                      /* Edit Form */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-white text-[10px] opacity-50">
                            Step {index + 1}/{workflow.totalSteps}
                          </span>
                          <span className="text-[#AAFF00] text-[10px] font-medium">Editing</span>
                        </div>

                        {/* Edit Title */}
                        <div>
                          <label className="block text-white text-[10px] opacity-70 mb-1">Title</label>
                          <input
                            type="text"
                            value={editForm.title}
                            onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded p-2 text-white text-[11px] outline-none focus:border-[#17A2B8]"
                            placeholder="Enter title"
                          />
                        </div>

                        {/* Change Due Date */}
                        <div>
                          <label className="block text-white text-[10px] opacity-70 mb-1">Due Date</label>
                          <input
                            type="date"
                            value={editForm.dueDate}
                            onChange={(e) => setEditForm({...editForm, dueDate: e.target.value})}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded p-2 text-white text-[11px] outline-none focus:border-[#17A2B8]"
                          />
                        </div>

                        {/* Edit Description */}
                        <div>
                          <label className="block text-white text-[10px] opacity-70 mb-1">Description</label>
                          <textarea
                            value={editForm.description}
                            onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                            className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded p-2 text-white text-[11px] outline-none focus:border-[#17A2B8] min-h-[60px]"
                            placeholder="Enter description"
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex gap-2 pt-2">
                          <button
                            onClick={() => handleSaveEdit(step.id)}
                            className="flex-1 bg-[#AAFF00] text-black text-[10px] font-medium py-2 rounded hover:bg-[rgba(170,255,0,0.9)] transition-all"
                          >
                            Save Changes
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="flex-1 bg-[rgba(255,255,255,0.1)] text-white text-[10px] font-medium py-2 rounded hover:bg-[rgba(255,255,255,0.2)] transition-all"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Normal View */
                      <>
                        {/* Step Header */}
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-white text-[10px] opacity-50">
                                Step {index + 1}/{workflow.totalSteps}
                              </span>
                              {isCompleted && (
                                <span className="text-green-500 text-[10px]">✓ Completed</span>
                              )}
                            </div>
                            <h3 className="text-white text-[12px] font-normal mb-1">
                              {step.title || 'Workflow Step'}
                            </h3>
                            <p className="text-white text-[10px] opacity-50">
                              {getDaysUntil(step.dueDate)}
                            </p>
                          </div>
                          
                          {/* Edit Button */}
                          <button
                            onClick={() => handleEditClick(step)}
                            className="text-[#AAFF00] text-[10px] px-2 py-1 hover:bg-[rgba(170,255,0,0.1)] rounded transition-colors"
                          >
                            Edit
                          </button>
                        </div>

                        {/* Step Description */}
                        {step.description && (
                          <p className="text-white text-[10px] opacity-70 mb-3">
                            {step.description}
                          </p>
                        )}

                        {/* Step Actions */}
                        {!isCompleted && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleCompleteStep(step.id)}
                              className="flex-1 bg-green-600 text-white text-[10px] font-medium py-2 rounded hover:bg-green-700 transition-all"
                            >
                              Completed
                            </button>
                            <button
                              onClick={() => handleSnoozeStep(step.id)}
                              className="flex-1 bg-[rgba(255,255,255,0.1)] text-white text-[10px] font-medium py-2 rounded hover:bg-[rgba(255,255,255,0.2)] transition-all"
                            >
                              Remind Later
                            </button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>

            {filteredSteps.length === 0 && (
              <div className="text-center py-12">
                <p className="text-white text-[12px] opacity-50">
                  No steps in this category
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </LayoutHeader>
  );
};

export default WorkflowDetail;


